import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LLMProvider } from "@/contexts/LLMContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { sorvegliaLaVersione } from "@/lib/versione";
import ErrorBoundary from "@/components/ErrorBoundary";
import ApiKeyDialog from "@/components/ApiKeyDialog";
import ErrorDialog from "@/components/ErrorDialog";
import Login from "@/pages/Login";
import Workspace from "@/pages/Workspace";
import OAuthCallback from "@/pages/OAuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import PendingVerification from "@/components/PendingVerification";
import NumaLogo from "@/components/NumaLogo";
import "@/App.css";

/**
 * L'attesa all'avvio, detta a voce.
 *
 * Un logo che pulsa e nient'altro è indistinguibile da un'applicazione rotta:
 * dopo qualche secondo bisogna dire che cosa si sta aspettando. Quasi sempre è
 * il server che si riaccende - l'hosting gratuito lo spegne dopo un quarto
 * d'ora di silenzio, e la prima richiesta dura quanto il risveglio.
 */
function Avvio() {
  const [secondi, setSecondi] = useState(0);
  useEffect(() => {
    const battito = setInterval(() => setSecondi((n) => n + 1), 1000);
    return () => clearInterval(battito);
  }, []);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper px-6 text-center"
         data-testid="avvio">
      <NumaLogo size={9} className="numa-mark animate-pulse" imageIndex={0} />
      <span className="sr-only">Caricamento in corso</span>
      {secondi >= 4 && (
        <p className="max-w-sm text-[13.5px] leading-relaxed text-ink-muted">
          {secondi < 12
            ? "Sto contattando il server…"
            : "Il server si stava riposando e si sta riaccendendo: ci vuole meno di un minuto."}
        </p>
      )}
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Avvio />;
  if (!user) return <Navigate to="/login" replace />;
  // An address still waiting to be confirmed gets one screen, not the
  // workspace: the API would refuse every call it makes anyway, and a product
  // that fails on every click is worse than one that explains itself.
  if (user.email_verified === false) return <PendingVerification />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Opened from the email, possibly in a browser that has never signed in. */}
      <Route path="/verifica" element={<VerifyEmail />} />
      <Route path="/reimposta" element={<ResetPassword />} />
      <Route path="/auth/openrouter" element={<ProtectedRoute><OAuthCallback /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Una versione nuova pubblicata mentre l'applicazione è aperta: si dice, e
  // si ricarica solo se chi sta scrivendo lo vuole.
  useEffect(() => sorvegliaLaVersione(() => {
    toast("È disponibile una versione aggiornata di NUMA.", {
      duration: Infinity,
      description: "Ricarica quando ti fa comodo: il lavoro è già salvato.",
      action: { label: "Ricarica", onClick: () => window.location.reload() },
    });
  }), []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <LLMProvider>
              <AppRouter />
              <ApiKeyDialog />
              <ErrorDialog />
              <Toaster position="bottom-right" closeButton />
            </LLMProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
